import { ChevronDown, Home, BedDouble, User, Clock, AlertTriangle } from "lucide-react";
import type { CSSProperties } from "react";
import type { CleaningJob } from "@shared/routes";

function isSameDayLocal(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

interface JobColorProps { className: string; style?: CSSProperties }

function BannerHeader({ count, expanded, onToggle }: { count: number; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-100/60 transition-colors"
      onClick={onToggle}
    >
      <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-150 ${expanded ? "" : "-rotate-90"}`} />
      {count} trabajo{count !== 1 ? "s" : ""} fuera del horario del calendario
    </button>
  );
}

export interface WeekOutOfRangeSectionProps {
  calendarFiltered: CleaningJob[];
  weekDays: Date[];
  today: Date;
  expanded: boolean;
  onToggle: () => void;
  dragOverCell: string | null;
  onDragOver: (cellKey: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, day: Date) => void;
  onDragStart: (e: React.DragEvent, jobId: number) => void;
  getJobColorProps: (job: CleaningJob) => JobColorProps;
  getAptName: (id: number | null | undefined) => string;
  getAptBedrooms: (id: number | null | undefined) => number | null;
  getAssigneeName: (userId: number | null | undefined, assignedOrgId?: number | null) => string;
  statusConfig: Record<string, { label: string }>;
  onJobClick: (job: CleaningJob) => void;
  calendarStartHour?: number;
  calendarEndHour?: number;
}

export function WeekOutOfRangeSection({
  calendarFiltered, weekDays, today, expanded, onToggle,
  dragOverCell, onDragOver, onDragLeave, onDrop, onDragStart,
  getJobColorProps, getAptName, getAptBedrooms, getAssigneeName,
  statusConfig, onJobClick,
  calendarStartHour = 6, calendarEndHour = 22,
}: WeekOutOfRangeSectionProps) {
  const jobsByDay = weekDays.map(day =>
    calendarFiltered.filter(j => {
      if (!j.scheduledDate) return false;
      const d = new Date(j.scheduledDate);
      if (!isSameDayLocal(new Date(d.getFullYear(), d.getMonth(), d.getDate()), day)) return false;
      const h = d.getHours();
      return h < calendarStartHour || h > calendarEndHour;
    })
  );
  const totalCount = jobsByDay.reduce((s, js) => s + js.length, 0);
  if (totalCount === 0) return null;

  return (
    <div className="border-b bg-amber-50/40">
      <BannerHeader count={totalCount} expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <div className="grid" style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
          <div className="border-r" />
          {weekDays.map((day, i) => {
            const outJobs = jobsByDay[i];
            const cellKey = `week-unsch-${i}`;
            return (
              <div
                key={i}
                className={`p-1 border-r last:border-r-0 min-h-[36px] transition-colors ${isSameDayLocal(day, today) ? "bg-primary/5" : ""} ${dragOverCell === cellKey ? "bg-primary/15 ring-1 ring-primary/40 ring-inset" : ""}`}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOver(cellKey); }}
                onDragLeave={onDragLeave}
                onDrop={e => onDrop(e, day)}
              >
                {(outJobs ?? []).map(job => {
                  const cp = getJobColorProps(job);
                  const jobTime = job.scheduledDate ? new Date(job.scheduledDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                  const statusLabel = (statusConfig[job.status] ?? statusConfig["pending"] ?? { label: "" }).label;
                  return (
                    <div
                      key={job.id}
                      draggable
                      onDragStart={e => onDragStart(e, job.id)}
                      className={`rounded px-1 py-0.5 mb-0.5 text-[10px] font-medium cursor-grab active:cursor-grabbing leading-tight ${cp.className}`}
                      style={cp.style}
                      title={`${getAptName(job.apartmentId)} — ${getAssigneeName(job.assignedTo, (job as any).assignedOrgId)}\n${statusLabel}\n${jobTime} (fuera de horario)`}
                      onClick={() => onJobClick(job)}
                      data-testid={`week-job-${job.id}`}
                    >
                      <div className="truncate">{getAptName(job.apartmentId)}{(() => { const br = getAptBedrooms(job.apartmentId); return br !== null ? <span className="opacity-75 ml-1">· {br === 0 ? "Studio" : `${br}BR`}</span> : null; })()}</div>
                      {jobTime && <div className="truncate opacity-75">{jobTime}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export interface DayOutOfRangeSectionProps {
  jobs: CleaningJob[];
  expanded: boolean;
  onToggle: () => void;
  onDragStart: (e: React.DragEvent, jobId: number) => void;
  getJobColorProps: (job: CleaningJob) => JobColorProps;
  getAptName: (id: number | null | undefined) => string;
  getAptBedrooms: (id: number | null | undefined) => number | null;
  getAssigneeName: (userId: number | null | undefined, assignedOrgId?: number | null) => string;
  onJobClick: (job: CleaningJob) => void;
}

export function DayOutOfRangeSection({
  jobs, expanded, onToggle, onDragStart,
  getJobColorProps, getAptName, getAptBedrooms, getAssigneeName, onJobClick,
}: DayOutOfRangeSectionProps) {
  if (jobs.length === 0) return null;
  return (
    <div className="border-b bg-amber-50/40">
      <BannerHeader count={jobs.length} expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <div className="flex flex-wrap gap-2 px-3 pb-3">
          {jobs.map(job => {
            const cp = getJobColorProps(job);
            const jobDate = new Date(job.scheduledDate!);
            const jobTime = jobDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            return (
              <div
                key={job.id}
                draggable
                onDragStart={e => onDragStart(e, job.id)}
                className={`rounded-md px-3 py-2 text-xs font-medium cursor-grab active:cursor-grabbing flex items-center gap-2 ${cp.className}`}
                style={cp.style}
                onClick={() => onJobClick(job)}
                data-testid={`day-job-outofrange-${job.id}`}
              >
                <Home className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[120px]">{getAptName(job.apartmentId)}</span>
                {(() => { const br = getAptBedrooms(job.apartmentId); return br !== null ? <span className="opacity-75 flex items-center gap-0.5 shrink-0"><BedDouble className="w-3 h-3" />{br === 0 ? "Studio" : `${br}BR`}</span> : null; })()}
                <span className="opacity-75">·</span>
                <Clock className="w-3 h-3 shrink-0" />
                <span className="shrink-0">{jobTime}</span>
                <span className="opacity-75">·</span>
                <User className="w-3 h-3 shrink-0" />
                <span>{getAssigneeName(job.assignedTo, (job as any).assignedOrgId).split(" ")[0]}</span>
                {job.priority === "urgent" && <AlertTriangle className="w-3 h-3 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
